/**
 * Universal Intake Demo Page
 * 
 * A demonstration and testing page for the Universal Intake Engine.
 * Shows how the toolbar and review mode work together.
 * 
 * This page can be used to test the intake engine with different
 * entity types before integrating into actual forms.
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { UniversalAIToolbar } from '../../components/ui/universal-ai-toolbar';
import { AIReviewMode } from '../../components/ui/ai-review-mode';
import { toast } from 'sonner';
import { 
  Sparkles, ArrowLeft, Users, UserPlus, FileText, 
  ClipboardList, Heart, ShieldCheck, AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import api from '../../lib/api';

// Supported entity types with icons
const ENTITY_TYPES = [
  { value: 'client', label: 'Client', icon: Users, description: 'Patient/client intake' },
  { value: 'employee', label: 'Employee', icon: UserPlus, description: 'Caregiver/staff' },
  { value: 'applicant', label: 'Applicant', icon: UserPlus, description: 'Job applicant' },
  { value: 'referral', label: 'Referral', icon: FileText, description: 'New client referral' },
  { value: 'assessment', label: 'Assessment', icon: ClipboardList, description: 'Client assessment' },
  { value: 'care_plan', label: 'Care Plan', icon: Heart, description: 'Care plan details' },
  { value: 'authorization', label: 'Authorization', icon: ShieldCheck, description: 'Service authorization' },
];

export default function UniversalIntakeDemo() {
  const navigate = useNavigate();
  
  const [entityType, setEntityType] = useState('client');
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionResult, setExtractionResult] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [activeTab, setActiveTab] = useState('extract');
  const [lastConfirmedData, setLastConfirmedData] = useState(null);
  
  // Handle extraction from toolbar
  const handleExtract = useCallback(async (extractionData) => {
    setIsExtracting(true);
    setExtractionResult(null);
    
    try {
      let response;
      
      if (extractionData.method === 'text') {
        // Text extraction (type, paste)
        response = await api.post('/intake/extract/text', {
          text: extractionData.content,
          entity_type: entityType,
          input_method: extractionData.inputMethod || 'natural_language',
        });
      } else if (extractionData.method === 'voice') {
        // Voice extraction
        response = await api.post('/intake/extract/voice', {
          audio_base64: extractionData.content,
          audio_format: extractionData.audioFormat || 'audio/webm',
          entity_type: entityType,
        });
      } else if (extractionData.method === 'document') {
        // Document upload
        const formData = new FormData();
        formData.append('file', extractionData.file);
        formData.append('entity_type', entityType);
        
        response = await api.post('/intake/extract/document', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else if (extractionData.method === 'image') {
        // Image/scan upload
        const formData = new FormData();
        formData.append('file', extractionData.file);
        formData.append('entity_type', entityType);
        
        response = await api.post('/intake/extract/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      
      if (response?.data?.success) {
        setExtractionResult(response.data);
        setActiveTab('review');
        toast.success(`Extracted ${response.data.fields?.length || 0} fields`);
      } else {
        toast.error(response?.data?.error || 'Extraction failed');
      }
    } catch (err) {
      console.error('Extraction error:', err);
      toast.error(err.response?.data?.detail || 'Extraction failed');
    } finally {
      setIsExtracting(false);
    }
  }, [entityType]);
  
  // Handle confirmation
  const handleConfirm = useCallback(async (confirmData) => {
    setIsConfirming(true);
    
    try {
      const response = await api.post('/intake/confirm', {
        extraction_id: confirmData.extraction_id,
        entity_type: entityType,
        approved_data: confirmData.approved_data,
        corrections: confirmData.corrections,
      });
      
      if (response?.data?.success) {
        setLastConfirmedData(confirmData.approved_data);
        setActiveTab('result');
        toast.success('Data confirmed successfully');
      } else {
        toast.error('Confirmation failed');
      }
    } catch (err) {
      console.error('Confirmation error:', err);
      toast.error(err.response?.data?.detail || 'Confirmation failed');
    } finally {
      setIsConfirming(false);
    }
  }, [entityType]);
  
  // Reset to start over
  const handleReset = useCallback(() => {
    setExtractionResult(null);
    setLastConfirmedData(null);
    setActiveTab('extract');
  }, []);
  
  const selectedEntity = ENTITY_TYPES.find(e => e.value === entityType);
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-semibold flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Universal Intake Engine
                </h1>
                <p className="text-sm text-muted-foreground">
                  AI-powered data entry — Type, Speak, Upload, Scan, or Paste
                </p>
              </div>
            </div>
            
            <Badge variant="outline">Demo Mode</Badge>
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="container max-w-4xl mx-auto px-4 py-8">
        {/* Entity type selector */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Select Entity Type</CardTitle>
            <CardDescription>
              Choose what kind of record you want to create
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={entityType} onValueChange={setEntityType}>
              <SelectTrigger className="w-full" data-testid="entity-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENTITY_TYPES.map((entity) => {
                  const Icon = entity.icon;
                  return (
                    <SelectItem key={entity.value} value={entity.value}>
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        <span>{entity.label}</span>
                        <span className="text-muted-foreground text-xs">
                          — {entity.description}
                        </span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
        
        {/* Main workflow tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="extract" className="flex items-center gap-2">
              <span className="h-5 w-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center">1</span>
              Extract
            </TabsTrigger>
            <TabsTrigger 
              value="review" 
              disabled={!extractionResult}
              className="flex items-center gap-2"
            >
              <span className="h-5 w-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center">2</span>
              Review
            </TabsTrigger>
            <TabsTrigger 
              value="result" 
              disabled={!lastConfirmedData}
              className="flex items-center gap-2"
            >
              <span className="h-5 w-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center">3</span>
              Result
            </TabsTrigger>
          </TabsList>
          
          {/* Step 1: Extract */}
          <TabsContent value="extract" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {selectedEntity && <selectedEntity.icon className="h-5 w-5" />}
                  Create {selectedEntity?.label || 'Record'}
                </CardTitle>
                <CardDescription>
                  Enter information using any method below. AI will extract structured data.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <UniversalAIToolbar
                  entityType={entityType}
                  onExtract={handleExtract}
                  isLoading={isExtracting}
                  placeholder={`Describe the ${selectedEntity?.label?.toLowerCase() || 'record'}... e.g., "John Smith, born March 15, 1945, lives at 123 Main Street, phone 555-123-4567..."`}
                />
              </CardContent>
            </Card>
            
            {/* Sample text hints */}
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
              <h4 className="font-medium text-sm mb-2">Sample text for testing:</h4>
              <p className="text-xs text-muted-foreground font-mono bg-background p-2 rounded">
                {entityType === 'client' && 
                  "John Smith was born March 15, 1945. He lives at 123 Main Street, Springfield, IL 62701. His phone is 555-123-4567 and email is john.smith@email.com. Primary diagnosis is COPD. Emergency contact is Mary Smith (wife) at 555-987-6543. He is funded by VA and authorized for 20 hours per week."}
                {entityType === 'employee' &&
                  "Maria Santos, email maria.santos@email.com, phone 555-234-5678. Born June 20, 1990. Lives at 456 Oak Avenue, Springfield, IL 62702. Hired January 15, 2024 as a Home Health Aide at $18/hour. Guardian number G123456, expires December 2025. CPR certified through March 2025. Speaks English and Spanish."}
                {entityType === 'applicant' &&
                  "Jane Doe applying for caregiver position. Phone 555-345-6789, email jane.doe@email.com. 5 years experience as a CNA. Available Monday through Friday, mornings. Has reliable transportation. Reference: Dr. Brown at 555-111-2222 (former supervisor)."}
                {entityType === 'referral' &&
                  "New referral from Springfield General Hospital for Robert Johnson, DOB 09/20/1938, discharged tomorrow. Contact case manager Sarah Lee at 555-444-5555. Patient has diabetes and CHF. Requesting personal care services, 4 hours daily. VA authorization pending."}
                {entityType === 'authorization' &&
                  "Authorization #VA-2024-12345 for John Smith from Veterans Affairs. Start date July 1, 2024 through December 31, 2024. Authorized for 20 hours per week of personal care services. Service code S5125 at $28/hour. Case manager: Tom Wilson at 555-666-7777."}
                {entityType === 'assessment' &&
                  "Assessment for John Smith conducted on January 15, 2024 by RN Sarah Johnson. Client is oriented x3. ADL status: needs assistance with bathing and dressing, independent with eating and toileting. Fall risk is moderate with one fall in past 6 months. Memory shows mild impairment. Recommended services: personal care 4 hours daily. Special caregiver requirements: experience with dementia patients."}
                {entityType === 'care_plan' &&
                  "Care Plan for John Smith effective February 1, 2024. Review date August 1, 2024. ADL services: bathing assistance, dressing assistance, grooming. IADL services: meal preparation, light housekeeping, medication reminders. Authorized 20 hours weekly, visit frequency 5 days per week. Goals: maintain independence, prevent falls, ensure medication compliance. Caregiver preference: female, English-speaking. Dietary restrictions: low sodium, diabetic diet."}
              </p>
            </div>
          </TabsContent>
          
          {/* Step 2: Review */}
          <TabsContent value="review" className="mt-6">
            {extractionResult ? (
              <AIReviewMode
                extractionResult={extractionResult}
                onConfirm={handleConfirm}
                onCancel={handleReset}
                isLoading={isConfirming}
                entityType={entityType}
              />
            ) : (
              <Card className="py-12">
                <CardContent className="text-center">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-muted-foreground">No extraction data yet</p>
                  <Button 
                    variant="link" 
                    onClick={() => setActiveTab('extract')}
                    className="mt-2"
                  >
                    Go to Extract step
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Step 3: Result */}
          <TabsContent value="result" className="mt-6">
            {lastConfirmedData ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="h-5 w-5" />
                    Data Confirmed
                  </CardTitle>
                  <CardDescription>
                    AI proposed. You approved. This data is ready to save.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-medium text-green-800 mb-3">Approved Data:</h4>
                    <div className="grid gap-2">
                      {Object.entries(lastConfirmedData).map(([key, value]) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="text-green-700 capitalize">
                            {key.replace(/_/g, ' ')}:
                          </span>
                          <span className="font-medium text-green-900">
                            {Array.isArray(value) ? value.join(', ') : value?.toString() || '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mt-6 flex justify-center">
                    <Button onClick={handleReset}>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Start New Intake
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="py-12">
                <CardContent className="text-center">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-muted-foreground">No confirmed data yet</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
        
        {/* Platform principle reminder */}
        <div className="mt-8 text-center text-xs text-muted-foreground">
          <Sparkles className="h-4 w-4 inline mr-1" />
          Platform Principle 15: &ldquo;Information Should Enter the Platform Naturally.&rdquo;
          <br />
          AI Assists. People Decide.
        </div>
      </div>
    </div>
  );
}
